import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, GitBranch, Megaphone } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { SectionCard } from '../components/common/SectionCard';
import { EmptyState } from '../components/common/EmptyState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { Button } from '../components/ui/Button';
import { Separator } from '../components/ui/Separator';
import { AiInsightRail } from '../components/ai/AiInsightRail';
import { DecisionDialog, type DecisionKind } from '../components/applications/DecisionDialog';
import { StageStepper } from '../components/lifecycle/StageStepper';
import { GatePanel } from '../components/lifecycle/GatePanel';
import { DocumentStatusBadge, RecordStatusPill, TrackPill } from '../components/common/StatusPills';
import { ScoreChip } from '../components/common/ScoreChip';
import { usePlatform } from '../contexts/PlatformContext';
import { entityById, entityName } from '../data/entities';
import { criteriaSets } from '../data/criteria';
import { formatAed, formatDate, daysUntil } from '../utils/format';
import { STAGE_META, trackLabel } from '../utils/stages';
export function ApplicationReview() {
  const {
    id = ''
  } = useParams();
  const {
    applicationById
  } = usePlatform();
  const record = applicationById(id);
  const [decision, setDecision] = React.useState<DecisionKind | null>(null);
  if (!record) {
    return <div className="p-6" data-id="element-1184" data-mp="sy508k4eqzbu">
        <EmptyState title="Record not found" description="This application reference does not exist in the current environment." action={<Link to="/applications" className="inline-flex items-center rounded-sm bg-uae-green px-3 py-2 text-[13px] font-medium text-white" data-id="element-1186" data-mp="k5tbr3sutlyi">
              Back to queue
            </Link>} data-id="element-1185" data-mp="sxbo13mojhmb" />
      </div>;
  }
  const entity = entityById(record.entityId);
  const criteriaSet = criteriaSets.find(set => set.track === record.track && set.stage === record.stage) ?? criteriaSets.find(set => set.track === record.track) ?? criteriaSets[0];
  const sla = daysUntil(record.slaDueOn);
  const isMegaMajor = record.track !== 'international';
  return <div className="pb-24" data-id="element-1187" data-mp="17lv6wfck1euo">
      <PageHeader eyebrow={`${trackLabel(record.track)} · ${record.ref}`} title={record.eventName} description={record.summary} actions={<div className="flex flex-wrap items-center gap-2" data-id="element-1189" data-mp="ysiah3fa7v3u">
            <RecordStatusPill status={record.status} data-id="element-1190" data-mp="ttot2zwvjjye" />
            <TrackPill track={record.track} data-id="element-1191" data-mp="tugmq56wv7m1" />
            <ScoreChip score={record.aiScore} showBand data-id="element-1192" data-mp="oum0wg1zt87u" />
            {isMegaMajor && <Link to={`/lifecycle/${record.id}`} className="inline-flex items-center rounded-sm border border-border bg-card px-3 py-2 text-[13px] font-medium transition-colors hover:bg-muted" data-id="element-1193" data-mp="osrcusvfczzj">
                <GitBranch className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-1194" data-mp="2rg7s5dblkevn" />
                Lifecycle workspace
              </Link>}
            {record.outreachId && <Link to="/outreach" className="inline-flex items-center rounded-sm border border-border bg-card px-3 py-2 text-[13px] font-medium transition-colors hover:bg-muted" data-id="element-1195" data-mp="30hfmjkazsvkg">
                <Megaphone className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-1196" data-mp="27ul4sv5k8aao" />
                Outreach campaign
              </Link>}
          </div>} data-id="element-1188" data-mp="17kk4aiuuid55" />

      <div className="space-y-4 p-6" data-id="element-1197" data-mp="2hr6bdkx89tgm">
        <StageStepper track={record.track} currentStage={record.stage} selectedStage={record.stage} daysInStage={record.daysInStage} onSelect={() => undefined} data-id="element-1198" data-mp="2hq52nn3opj22" />

        <div className="grid gap-4 xl:grid-cols-[1.75fr_1fr]" data-id="element-1199" data-mp="1p0y1egcuifd4">
          <div className="space-y-4" data-id="element-1200" data-mp="1u0ap13bjjqgr">
            <Tabs defaultValue="overview" data-id="element-1201" data-mp="3vr14mqmkiv15">
              <TabsList className="flex-wrap" data-id="element-1202" data-mp="3vs2qzr91b3y6">
                <TabsTrigger value="overview" data-id="element-1203" data-mp="3voomaccweink">Overview</TabsTrigger>
                <TabsTrigger value="entity" data-id="element-1204" data-mp="3rr0sjcmxyvx9">Entity &amp; governance</TabsTrigger>
                <TabsTrigger value="event" data-id="element-1205" data-mp="9chzlkjhgs7c">Event profile</TabsTrigger>
                <TabsTrigger value="budget" data-id="element-1206" data-mp="4kzxuu9mynlb">Budget &amp; funding</TabsTrigger>
                <TabsTrigger value="documents" data-id="element-1207" data-mp="4iwznmybxo5i">Documents</TabsTrigger>
                <TabsTrigger value="timeline" data-id="element-1208" data-mp="j830tggq9og1">Timeline &amp; audit</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4 space-y-4" data-id="element-1209" data-mp="ea9umnffcufr">
                <SectionCard title="Record summary" data-id="element-1210" data-mp="s99elhrzsl5l">
                  <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1211" data-mp="nb7z16trz3yv">
                    <div data-id="element-1212" data-mp="n94znw6qfyhh">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1213" data-mp="2u1p7a5vzkfdc">
                        Current stage
                      </dt>
                      <dd className="mt-1 text-[13px]" data-id="element-1214" data-mp="2p4fc09lx19hl">
                        {STAGE_META[record.stage].label} · {record.daysInStage} days
                      </dd>
                    </div>
                    <div data-id="element-1215" data-mp="19iv9b5g50h1u">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1216" data-mp="19lhj7bqi38n8">
                        Assigned officer
                      </dt>
                      <dd className="mt-1 text-[13px]" data-id="element-1217" data-mp="zsaapi0grcix">{record.officer}</dd>
                    </div>
                    <div data-id="element-1218" data-mp="lsr62z6v2jbd">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1219" data-mp="lwey1twnqxnz">
                        Service standard
                      </dt>
                      <dd className={`mt-1 text-[13px] ${sla < 0 ? 'text-uae-red' : ''}`} data-id="element-1220" data-mp="cuysq0kp9lqm">
                        {sla < 0 ? `${Math.abs(sla)} days over (${formatDate(record.slaDueOn)})` : `${sla} days remaining (${formatDate(record.slaDueOn)})`}
                      </dd>
                    </div>
                    <div data-id="element-1221" data-mp="3k7i8prsgskvj">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1222" data-mp="3k3krjbpg63ea">
                        Host body
                      </dt>
                      <dd className="mt-1 text-[13px]" data-id="element-1223" data-mp="3b623rzkf5ayp">{record.hostBody}</dd>
                    </div>
                    <div data-id="element-1224" data-mp="367qpncn3ceao">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1225" data-mp="368in6t090m3l">
                        Edition year
                      </dt>
                      <dd className="mt-1 font-mono text-[13px]" data-id="element-1226" data-mp="2qqnstjs8zvd">{record.editionYear}</dd>
                    </div>
                    <div data-id="element-1227" data-mp="3u2a9t5l99pv2">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1228" data-mp="3p51owx8q37q4">
                        Criteria set applied
                      </dt>
                      <dd className="mt-1 text-[13px]" data-id="element-1229" data-mp="qgco5kbw5l4y">{criteriaSet.label}</dd>
                    </div>
                  </dl>
                </SectionCard>

                <GatePanel record={record} stage={record.stage} data-id="element-1230" data-mp="t09aarpm6aqw" />
              </TabsContent>

              <TabsContent value="entity" className="mt-4 space-y-4" data-id="element-1231" data-mp="xy127lq9dwv3">
                <SectionCard title="Applying entity" description={entityName(record.entityId)} data-id="element-1232" data-mp="y0dq0fip71g2">
                  {entity ? <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1233" data-mp="16zqv6vzsh3uz">
                      <div data-id="element-1234" data-mp="1bybwkyqs1tl0">
                        <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1235" data-mp="1bz3n3v5ip14f">
                          Entity type
                        </dt>
                        <dd className="mt-1 text-[13px]" data-id="element-1236" data-mp="1lo585zqx68p9">{entity.kind}</dd>
                      </div>
                      <div data-id="element-1237" data-mp="1qmykhougztnu">
                        <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1238" data-mp="1qm6m669hnhtv">
                          Emirate
                        </dt>
                        <dd className="mt-1 text-[13px]" data-id="element-1239" data-mp="1znwgbjur8uvc">{entity.emirate}</dd>
                      </div>
                      <div data-id="element-1240" data-mp="2kyl7lo0n3639">
                        <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1241" data-mp="2kxtmj74o4qr8">
                          Contact
                        </dt>
                        <dd className="mt-1 text-[13px]" data-id="element-1242" data-mp="2uwr75yyz404y">{entity.contact}</dd>
                      </div>
                      <div data-id="element-1243" data-mp="2py7m00s7esdn">
                        <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1244" data-mp="2pyzcix6y1zx2">
                          Bid record
                        </dt>
                        <dd className="mt-1 font-mono text-[13px]" data-id="element-1245" data-mp="1x9inm9ij915l">
                          {entity.bidsWon} won of {entity.bidsSubmitted} submitted
                        </dd>
                      </div>
                      <div data-id="element-1246" data-mp="276wtxgaqs9k6">
                        <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1247" data-mp="276ojdv23dgc5">
                          Support received to date
                        </dt>
                        <dd className="mt-1 font-mono text-[13px]" data-id="element-1248" data-mp="1ehpxcmuulu1k">
                          {formatAed(entity.supportReceivedAed)}
                        </dd>
                      </div>
                      <div data-id="element-1249" data-mp="3ux4fj0e528le">
                        <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1250" data-mp="3uyfiwz3o9ebz">
                          Compliance standing
                        </dt>
                        <dd className={`mt-1 text-[13px] capitalize ${entity.compliance === 'good-standing' ? 'text-uae-leaf' : entity.compliance === 'restricted' ? 'text-uae-red' : 'text-uae-bronze'}`} data-id="element-1251" data-mp="3l69prm1ooqv7">
                          {entity.compliance.replace('-', ' ')}
                        </dd>
                      </div>
                    </dl> : <p className="text-[12px] text-muted-foreground" data-id="element-1252" data-mp="2sjf9707zmbn0">
                      Entity record unavailable.
                    </p>}
                </SectionCard>
              </TabsContent>

              <TabsContent value="event" className="mt-4 space-y-4" data-id="element-1253" data-mp="1hopya2oy2v9q">
                <SectionCard title="Event profile" data-id="element-1254" data-mp="13ymgbnx3lnq3">
                  <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1255" data-mp="13vqgfu1owoz0">
                    <div data-id="element-1256" data-mp="17xvbmx7sdtnr">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1257" data-mp="17x21rndj6o1e">
                        Sector
                      </dt>
                      <dd className="mt-1 text-[13px]" data-id="element-1258" data-mp="yzj9pz1gz9g0">{record.sector}</dd>
                    </div>
                    <div data-id="element-1259" data-mp="l0a5lxqjg5se">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1260" data-mp="l11t0l9zex7l">
                        Host emirate
                      </dt>
                      <dd className="mt-1 text-[13px]" data-id="element-1261" data-mp="2fe1ayl1u43i1">{record.emirate}</dd>
                    </div>
                    <div data-id="element-1262" data-mp="2jda71wkckdgr">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1263" data-mp="2jds959toxlwl">
                        Track
                      </dt>
                      <dd className="mt-1 text-[13px]" data-id="element-1264" data-mp="3qfsy3reyee3">{trackLabel(record.track)}</dd>
                    </div>
                  </dl>
                  <Separator className="my-4" data-id="element-1265" data-mp="hicdnmqr5m0g" />
                  <p className="text-[13px] leading-relaxed text-muted-foreground" data-id="element-1266" data-mp="cpt1mu7pqyxr">
                    {record.summary}
                  </p>
                </SectionCard>
              </TabsContent>

              <TabsContent value="budget" className="mt-4 space-y-4" data-id="element-1267" data-mp="mgnnhxh1rwio">
                <SectionCard title="Budget & funding" data-id="element-1268" data-mp="migvbg3ecms9">
                  <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1269" data-mp="10em1h1rghqxn">
                    <div data-id="element-1270" data-mp="vgaoii5v70b7">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1271" data-mp="vi3yo7fos2ny">
                        Support requested
                      </dt>
                      <dd className="mt-1 font-mono text-lg font-semibold" data-id="element-1272" data-mp="2x8p9jq3pgkvu">
                        {formatAed(record.requestedSupportAed)}
                      </dd>
                    </div>
                    <div data-id="element-1273" data-mp="163ly2miq5nt2">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1274" data-mp="1656w4m48zovo">
                        Funding mandate
                      </dt>
                      <dd className="mt-1 font-mono text-lg font-semibold" data-id="element-1275" data-mp="1f4k1p3p93tb4">
                        {record.approval ? formatAed(record.approval.mandateAed) : 'Not yet issued'}
                      </dd>
                    </div>
                    <div data-id="element-1276" data-mp="1ttq1otvwxnk2">
                      <dt className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1277" data-mp="1tt6lpiptp98o">
                        Drawn to date
                      </dt>
                      <dd className="mt-1 font-mono text-lg font-semibold" data-id="element-1278" data-mp="1yum390sskxmb">
                        {record.planBuild ? formatAed(record.planBuild.budgetDrawnAed) : 'Nil'}
                      </dd>
                    </div>
                  </dl>
                  {record.approval && record.approval.conditions.length > 0 && <>
                      <Separator className="my-4" data-id="element-1279" data-mp="3jatzwjccfco1" />
                      <p className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground" data-id="element-1280" data-mp="3jbmz8z8z2zw5">
                        Conditions attached to funding
                      </p>
                      <ul className="mt-2 space-y-2" data-id="element-1281" data-mp="ewvj54kao6in">
                        {record.approval.conditions.map(condition => <li key={condition} className="rounded-sm border border-border bg-muted/50 px-3 py-2 text-[12px]" data-id="element-1282" data-mp="avjy7ccd89gs">
                            {condition}
                          </li>)}
                      </ul>
                    </>}
                </SectionCard>
              </TabsContent>

              <TabsContent value="documents" className="mt-4" data-id="element-1283" data-mp="ts1sa0eoslg7">
                <SectionCard title="Documents" description="Verification state of every artefact attached to the record" bodyClassName="p-0" data-id="element-1284" data-mp="dctxo4xwwwtl">
                  <ul className="divide-y divide-border" data-id="element-1285" data-mp="4fjocp4nnhrb">
                    {record.documents.map(document => <li key={document.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-id="element-1286" data-mp="9emopw9owzsy">
                        <div className="min-w-0" data-id="element-1287" data-mp="1162bprf5sojp">
                          <p className="truncate text-[13px]" data-id="element-1288" data-mp="ndcppmzmrjml">{document.label}</p>
                          <p className="text-[11px] text-muted-foreground" data-id="element-1289" data-mp="sbe9690yxkyl">
                            Updated {formatDate(document.updatedOn)}
                          </p>
                        </div>
                        <DocumentStatusBadge status={document.status} data-id="element-1290" data-mp="1ksgspev8ggsb" />
                      </li>)}
                  </ul>
                </SectionCard>
              </TabsContent>

              <TabsContent value="timeline" className="mt-4" data-id="element-1291" data-mp="1q5oxl7rqi4gu">
                <SectionCard title="Timeline & audit" description="Every decision, advancement and override attributed to its author" data-id="element-1292" data-mp="1q32p95x0rkxk">
                  <ol className="relative space-y-4 border-l border-border pl-5" data-id="element-1293" data-mp="1yy8yyfzeun5d">
                    {[...record.timeline].reverse().map(event => <li key={event.id} className="relative" data-id="element-1294" data-mp="176t7o8mwsc8t">
                        <span className="absolute -left-[23px] top-1 h-2 w-2 rounded-full bg-uae-green" aria-hidden="true" data-id="element-1295" data-mp="178ns7lu9q2cd" />
                        <p className="text-[13px] font-medium" data-id="element-1296" data-mp="1g9c4oc40jun5">{event.label}</p>
                        <p className="text-[11px] text-muted-foreground" data-id="element-1297" data-mp="2wjeiqtowcemt">
                          {formatDate(event.on)} · {event.actor}
                        </p>
                        {event.detail && <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground" data-id="element-1298" data-mp="2zp6sbrwaep3p">
                            {event.detail}
                          </p>}
                      </li>)}
                  </ol>
                </SectionCard>
              </TabsContent>
            </Tabs>
          </div>

          <AiInsightRail record={record} bands={criteriaSet.bands} criteriaSetLabel={criteriaSet.label} data-id="element-1299" data-mp="2fzwc8csmq252" />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t border-border bg-card/95 px-6 py-3 backdrop-blur lg:left-60" data-id="element-1300" data-mp="2pwiv4w1eaj4g">
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-1301" data-mp="2uosratt3sff6">
          <p className="text-[12px] text-muted-foreground" data-id="element-1302" data-mp="1x8vhk0yjksrb">
            Decisions are recorded against{' '}
            <span className="font-mono" data-id="element-1303" data-mp="226okml4jt6nd">{record.ref}</span> with author and rationale.
          </p>
          <div className="flex flex-wrap items-center gap-2" data-id="element-1304" data-mp="1uo7969d43hrr">
            <Button variant="outline" size="sm" onClick={() => setDecision('request-info')} data-id="element-1305" data-mp="1poumppq5kcpp">
              Request information
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDecision('escalate')} data-id="element-1306" data-mp="1kogcc112wqoi">
              Escalate to committee
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setDecision('reject')} data-id="element-1307" data-mp="1ft0y37z5ssrk">
              Decline
            </Button>
            <Button size="sm" onClick={() => setDecision('approve')} data-id="element-1308" data-mp="16y33zxk9z9yn">
              Approve at stage
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-1309" data-mp="11ygthvvu07tj" />
            </Button>
          </div>
        </div>
      </div>

      <DecisionDialog record={record} kind={decision} onClose={() => setDecision(null)} data-id="element-1310" data-mp="x202mtx8zfcr" />
    </div>;
}